import React, { useEffect, useMemo } from 'react';
import { Field, change } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Label } from 'reactstrap';
import { Utils } from '../../services';
import Brick from './BrickCore';

const ACTIVATION_MODE_ALWAYS = 'always';
const ACTIVATION_MODE_IF_PRICING_ZERO = 'if_pricing_zero';
const ACTIVATION_MODE_NEVER = 'never';

/**
 * Options-tab only (not shown in inputs_mapping).
 * `type_id` / `pricing_key` stay mappable so form-driven flows (e.g. accreditation
 * `tickettype_pricing`) can resolve them from context; static options still win at runtime.
 */
const TICKET_CREATE_OPTION_INPUTS = new Set([
    'activation_mode',
    'edition',
    'api_key',
]);

const TicketCreateOptionsForm = ({ t, position, tickettypes }) => {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const options = useSelector(
        (state) => state.form?.workflowForm?.values?.bricks?.[position]?.options || {}
    );

    const type_id = options.type_id || '';
    const activation_mode = options.activation_mode || ACTIVATION_MODE_ALWAYS;
    const pricing_key = options.pricing_key || '';

    const type_options = useMemo(() => {
        return (tickettypes || [])
            .map((tickettype) => ({
                value: String(tickettype?._id || ''),
                label: Utils.localized_or_fallback(tickettype?.name, i18n.language, tickettype?._id)
                    || String(tickettype?._id || '')
            }))
            .filter((opt) => opt.value !== '')
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [tickettypes, i18n.language]);

    const pricing_options = useMemo(() => {
        const tickettype = (tickettypes || []).find((tt) => String(tt?._id || '') === String(type_id));
        const pricings = tickettype?.pricings || {};

        return Object.keys(pricings).map((key) => ({
            value: key,
            label: Utils.localized_or_fallback(pricings[key]?.name, i18n.language, key) || key
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [tickettypes, type_id, i18n.language]);

    useEffect(() => {
        if (activation_mode === ACTIVATION_MODE_NEVER && pricing_key !== '') {
            dispatch(change('workflowForm', `bricks[${position}].options.pricing_key`, ''));
            return;
        }

        const is_valid_pricing = pricing_options.some((opt) => opt.value === pricing_key);
        if (pricing_key !== '' && !is_valid_pricing) {
            dispatch(change('workflowForm', `bricks[${position}].options.pricing_key`, ''));
        }
    }, [dispatch, position, activation_mode, pricing_key, pricing_options]);

    const prefix = `bricks[${position}].options`;
    const label = (key) => t(`workflows.bricks.ticket.create.options.${key}`);

    return (
        <>
            <FormGroup row>
                <Label for="type_id" className="text-v-center" sm={12}>
                    {label('type_id')}
                </Label>
                <Col sm={12}>
                    <Field component="select" name={`${prefix}.type_id`} className="form-control">
                        <option value="">{label('type_id_placeholder')}</option>
                        {type_options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Field>
                </Col>
            </FormGroup>

            <FormGroup row>
                <Label for="activation_mode" className="text-v-center" sm={12}>
                    {label('activation_mode')}
                </Label>
                <Col sm={12}>
                    <Field
                        component="select"
                        name={`${prefix}.activation_mode`}
                        className="form-control"
                        format={(value) => value || ACTIVATION_MODE_ALWAYS}
                    >
                        <option value={ACTIVATION_MODE_ALWAYS}>{label('activation_mode_always')}</option>
                        <option value={ACTIVATION_MODE_IF_PRICING_ZERO}>
                            {label('activation_mode_if_pricing_zero')}
                        </option>
                        <option value={ACTIVATION_MODE_NEVER}>{label('activation_mode_never')}</option>
                    </Field>
                </Col>
            </FormGroup>

            {activation_mode !== ACTIVATION_MODE_NEVER ? (
                <FormGroup row>
                    <Label for="pricing_key" className="text-v-center" sm={12}>
                        {label('pricing_key')}
                    </Label>
                    <Col sm={12}>
                        <Field component="select" name={`${prefix}.pricing_key`} className="form-control">
                            <option value="">{label('pricing_key_placeholder')}</option>
                            {pricing_options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Field>
                    </Col>
                </FormGroup>
            ) : null}

            <FormGroup row>
                <Label for="api_key" className="text-v-center" sm={12}>
                    {label('api_key')}
                </Label>
                <Col sm={12}>
                    <Field
                        component="input"
                        type="text"
                        name={`${prefix}.api_key`}
                        className="form-control"
                        placeholder={label('api_key_placeholder')}
                    />
                </Col>
            </FormGroup>
        </>
    );
};

class TicketCreateBrick extends Brick {
    /**
     * Contact/context fields only — type, activation and API key belong to the options tab.
     *
     * @returns {string[]}
     */
    getInputs() {
        return (this.inputs || []).filter((input) => !TICKET_CREATE_OPTION_INPUTS.has(input));
    }

    /**
     * @param {{ tickettypes?: object[], t?: Function }|null|undefined} detailCtx
     * @returns {string|null}
     */
    getNodeDetails(detailCtx) {
        const tickettypes = detailCtx?.tickettypes || [];
        const t = detailCtx?.t;
        const typeId = this.getOption('type_id');
        const activationMode = this.getOption('activation_mode');
        const pricingKey = this.getOption('pricing_key');
        const parts = [];

        const tickettype = typeId != null
            ? tickettypes.find((tt) => String(tt?._id) === String(typeId))
            : null;

        if (tickettype) {
            const typeLabel = this.localizedDetailLabel(tickettype?.name, detailCtx, '');
            if (typeLabel)
                parts.push(typeLabel);
        }

        if (activationMode != null) {
            const mode = String(activationMode);
            const modeLabel = mode === ACTIVATION_MODE_ALWAYS
                ? (t ? t('workflows.bricks.ticket.create.options.activation_mode_always') : 'Always')
                : mode === ACTIVATION_MODE_IF_PRICING_ZERO
                    ? (t ? t('workflows.bricks.ticket.create.options.activation_mode_if_pricing_zero') : 'If free')
                    : mode === ACTIVATION_MODE_NEVER
                        ? (t ? t('workflows.bricks.ticket.create.options.activation_mode_never') : 'Never')
                        : '';
            if (modeLabel)
                parts.push(modeLabel);
        }

        if (pricingKey != null && String(pricingKey).trim() !== '') {
            const pricing = tickettype?.pricings?.[String(pricingKey)];
            const pricingLabel = this.localizedDetailLabel(pricing?.name, detailCtx, '');
            if (pricingLabel)
                parts.push(pricingLabel);
        }

        return this.joinNodeDetailParts(parts);
    }

    getOptionsForm(props) {
        return (
            <TicketCreateOptionsForm
                t={props.t}
                position={this.position}
                tickettypes={props.tickettypes || []}
            />
        );
    }
}

export default TicketCreateBrick;
